/**
 * Keeps float-mode badges fully inside the viewport **horizontally**.
 *
 * Merchant offsets (`--inset-inline-start`, `--inset-inline-start-mobile`) are
 * %-based (-10…110%) relative to the positioned parent. On narrow viewports (or
 * when the parent sits near a screen edge), extreme offsets push the badge off-screen.
 *
 * Fix: compute `--badge-clamp-min-inline` / `--badge-clamp-max-inline` (px) so that
 * CSS `clamp(min, merchantOffset%, max)` on `inset-inline-start` always keeps the
 * full badge box inside `[MARGIN, viewportWidth - MARGIN]`.
 *
 * Vertical axis is NOT clamped: badge moves with its absolute context during scroll,
 * and clamping `top` produced multi-thousand px corrections when the section was
 * off-screen.
 *
 * Every badge on the page shares one ResizeObserver and one pass a frame. What moves a
 * badge sideways is a change of width: its own, its offset parent's, or the root's (the
 * viewport, or a scrollbar coming or going). The observer reports those, and a change of
 * height (the page growing as its images load) is let go. The pass reads every badge
 * marked, then writes, and only the values that differ.
 */

const CLAMP_MIN_VAR = "--badge-clamp-min-inline";
const CLAMP_MAX_VAR = "--badge-clamp-max-inline";
const VIEWPORT_MARGIN = 8;

/** @typedef {{ min: string, max: string }} Clamp */

export class BadgeFloat extends HTMLElement {
  /** @type {Set<BadgeFloat>} */
  static #badges = new Set();
  /** @type {Set<BadgeFloat>} Badges to work out in the next pass */
  static #dirty = new Set();
  /** @type {Map<Element, Set<BadgeFloat>>} Each offset parent observed -> the badges placed in it */
  static #parents = new Map();
  /** @type {WeakMap<Element, number>} Each observed box's width, as last seen */
  static #widths = new WeakMap();
  /** @type {ResizeObserver | undefined} */
  static #observer;
  static #frame = 0;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Element | null} The offset parent it's observed in */
  #parent = null;

  /** @type {boolean | undefined} */
  #rtl;

  /** @type {Clamp | null} What it last wrote */
  #written = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (!BadgeFloat.#observer) {
      BadgeFloat.#observer = new ResizeObserver(BadgeFloat.#onResize);
      BadgeFloat.#observer.observe(document.documentElement);
    }
    BadgeFloat.#badges.add(this);
    // Its first report (once it's drawn) has it worked out
    BadgeFloat.#observer.observe(this);

    if (window.Shopify?.designMode) {
      const section = this.closest(".shopify-section");
      section?.addEventListener(
        "shopify:section:load",
        () => {
          BadgeFloat.#dirty.add(this);
          BadgeFloat.#schedule();
        },
        { signal },
      );
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    BadgeFloat.#badges.delete(this);
    BadgeFloat.#dirty.delete(this);
    BadgeFloat.#observer?.unobserve(this);
    this.#placeIn(null);
    this.#rtl = undefined;
    this.#written = null;
    if (!BadgeFloat.#badges.size) {
      // The last one gone (a page left behind): the next page starts afresh
      BadgeFloat.#observer?.disconnect();
      BadgeFloat.#observer = undefined;
      BadgeFloat.#parents.clear();
      cancelAnimationFrame(BadgeFloat.#frame);
      BadgeFloat.#frame = 0;
    }
    this.style.removeProperty(CLAMP_MIN_VAR);
    this.style.removeProperty(CLAMP_MAX_VAR);
  }

  /** @param {ResizeObserverEntry[]} entries */
  static #onResize = (entries) => {
    for (const { target, borderBoxSize, contentRect } of entries) {
      const width = borderBoxSize?.[0]?.inlineSize ?? contentRect.width;
      const last = BadgeFloat.#widths.get(target);
      BadgeFloat.#widths.set(target, width);
      if (last !== undefined && Math.abs(width - last) < 0.5) continue;

      if (target === document.documentElement) {
        for (const badge of BadgeFloat.#badges) BadgeFloat.#dirty.add(badge);
      } else if (target instanceof BadgeFloat) {
        BadgeFloat.#dirty.add(target);
      }
      for (const badge of BadgeFloat.#parents.get(target) ?? []) BadgeFloat.#dirty.add(badge);
    }
    BadgeFloat.#schedule();
  };

  static #schedule() {
    if (!BadgeFloat.#frame && BadgeFloat.#dirty.size) BadgeFloat.#frame = requestAnimationFrame(BadgeFloat.#pass);
  }

  /** Every badge marked is read, then written */
  static #pass = () => {
    BadgeFloat.#frame = 0;
    const viewportWidth = window.innerWidth;
    /** @type {[BadgeFloat, Clamp | null | undefined][]} */
    const results = [];
    for (const badge of BadgeFloat.#dirty) {
      if (badge.isConnected) results.push([badge, badge.#measure(viewportWidth)]);
    }
    BadgeFloat.#dirty.clear();
    for (const [badge, clamp] of results) badge.#apply(clamp);
  };

  /**
   * Observes the offset parent it's in, with the other badges in it.
   * @param {Element | null} parent
   */
  #placeIn(parent) {
    if (parent === this.#parent) return;
    const parents = BadgeFloat.#parents;
    const previous = this.#parent && parents.get(this.#parent);
    if (previous) {
      previous.delete(this);
      if (!previous.size) {
        parents.delete(this.#parent);
        BadgeFloat.#observer?.unobserve(this.#parent);
      }
    }
    this.#parent = parent;
    if (!parent) return;
    let placed = parents.get(parent);
    if (!placed) {
      parents.set(parent, (placed = new Set()));
      BadgeFloat.#observer?.observe(parent);
    }
    placed.add(this);
  }

  /**
   * Where it can go: layout reads only, so a pass reads every badge before it writes any.
   * @param {number} vw The viewport's width
   * @returns {Clamp | null | undefined} The clamp; null to take it off; undefined to leave it be
   */
  #measure(vw) {
    const parent = this.offsetParent;
    const observed = parent instanceof Element && parent !== document.documentElement ? parent : null;
    this.#placeIn(observed);
    if (!(parent instanceof Element)) return undefined;

    const parentRect = parent.getBoundingClientRect();
    // The observer's first report on a parent is this width: it needs no second pass
    if (observed && !BadgeFloat.#widths.has(observed)) BadgeFloat.#widths.set(observed, parentRect.width);

    if (parentRect.right <= 0 || parentRect.left >= vw) return null;

    const badgeWidth = this.getBoundingClientRect().width;
    if (badgeWidth < 1) return undefined;

    this.#rtl ??= getComputedStyle(this).direction === "rtl";

    let min;
    let max;

    if (this.#rtl) {
      min = parentRect.right - vw + VIEWPORT_MARGIN;
      max = parentRect.right - VIEWPORT_MARGIN - badgeWidth;
    } else {
      min = VIEWPORT_MARGIN - parentRect.left;
      max = vw - VIEWPORT_MARGIN - parentRect.left - badgeWidth;
    }

    if (min > max) {
      const mid = (min + max) / 2;
      min = mid;
      max = mid;
    }

    const r = (n) => Math.round(n * 100) / 100;
    return { min: `${r(min)}px`, max: `${r(max)}px` };
  }

  /** @param {Clamp | null | undefined} clamp What #measure found, written where it differs */
  #apply(clamp) {
    if (clamp === undefined) return;
    if (clamp === null) {
      if (!this.#written) return;
      this.style.removeProperty(CLAMP_MIN_VAR);
      this.style.removeProperty(CLAMP_MAX_VAR);
      this.#written = null;
      return;
    }
    if (clamp.min !== this.#written?.min) this.style.setProperty(CLAMP_MIN_VAR, clamp.min);
    if (clamp.max !== this.#written?.max) this.style.setProperty(CLAMP_MAX_VAR, clamp.max);
    this.#written = clamp;
  }
}

if (!customElements.get("badge-float")) {
  customElements.define("badge-float", BadgeFloat);
}
